import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getProject, getNextProject } from "@/data/projects";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = getProject(id);
  const next = project ? getNextProject(project.id) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, [id]);

  if (!project) {
    return (
      <main className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6 pt-32">
          <div className="text-center">
            <h1 className="font-display text-5xl">Project not found</h1>
            <p className="mt-4 text-[#1A1A1A]/70">
              This case study doesn't exist yet. Head back and pick another.
            </p>
            <button
              onClick={() => navigate("/#projects")}
              className="tb-pill tb-pill-primary mt-8"
            >
              Back to projects
            </button>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="relative bg-[#FDFCF9]">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 border-b border-[#1A1A1A]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <button
            onClick={() => navigate("/#projects")}
            className="inline-flex items-center gap-2 text-sm font-mono tracking-widest uppercase text-[#1A1A1A]/60 hover:text-[#E9298A] transition-colors group"
            data-testid="project-back-link"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            All projects
          </button>

          <div className="mt-8 md:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/50">
                Project · {project.year || "—"}
              </div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="mt-4 font-display font-black tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[0.95] text-[#1A1A1A]"
                data-testid="project-title"
              >
                {project.name}
              </motion.h1>
              <p className="mt-6 text-lg md:text-2xl text-[#1A1A1A]/75 max-w-2xl leading-snug">
                {project.tagline}
              </p>
            </div>
            <div className="lg:col-span-4 lg:pt-6">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/50">
                Skills
              </div>
              <ul className="mt-4 space-y-2 text-[#1A1A1A]">
                {project.skills.map((s) => (
                  <li key={s} className="text-base md:text-lg font-semibold">
                    {s}
                  </li>
                ))}
              </ul>

              {project.url && project.url !== "#" && (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-visit-link"
                  className="mt-8 tb-pill tb-pill-primary"
                >
                  Visit website
                  <span className="w-7 h-7 -mr-1 rounded-full bg-[#FFE600] text-[#1A1A1A] flex items-center justify-center">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </span>
                </a>
              )}
              {(!project.url || project.url === "#") && (
                <div className="mt-8 tb-sticker">Coming soon</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Hero screenshot */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-3xl overflow-hidden border border-[#1A1A1A] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.25)]"
          >
            <img
              src={project.img}
              alt={project.name}
              className="w-full h-auto block"
              loading="eager"
            />
          </motion.div>
        </div>
      </section>

      {/* Brief + highlights */}
      <section className="pb-20 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="tb-eyebrow">
              <span className="dot" />
              The brief
            </span>
            <h2 className="mt-6 font-display font-black tracking-tighter text-3xl md:text-5xl leading-[1] text-[#1A1A1A]">
              What we made.{" "}
              <span className="text-[#E9298A]">Why it works.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-4">
            <p className="text-lg md:text-xl text-[#1A1A1A]/85 leading-relaxed whitespace-pre-line">
              {project.brief}
            </p>

            <div className="mt-10">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/50 mb-4">
                Highlights
              </div>
              <ul className="space-y-4">
                {project.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-base md:text-lg">
                    <span className="mt-1 w-6 h-6 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span className="text-[#1A1A1A]">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {project.gallery && project.gallery.length > 0 && (
        <section className="pb-24 md:pb-32">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between gap-6 mb-10 md:mb-14">
              <div>
                <span className="tb-eyebrow">
                  <span className="dot" />
                  Gallery
                </span>
                <h3 className="mt-6 font-display font-black tracking-tighter text-3xl md:text-5xl leading-[1] text-[#1A1A1A]">
                  A closer look.
                </h3>
              </div>
              <div className="font-mono text-xs text-[#1A1A1A]/50 tabular-nums pb-1">
                {String(project.gallery.length).padStart(2, "0")} shots
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {project.gallery.map((src, i) => (
                <motion.figure
                  key={src}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: (i % 2) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className={`relative overflow-hidden rounded-3xl border border-[#1A1A1A]/15 bg-[#F3F1EC] shadow-[0_24px_60px_-30px_rgba(0,0,0,0.25)] ${
                    i === 0 && project.gallery.length % 2 === 1 ? "md:col-span-2" : ""
                  }`}
                  data-testid={`project-gallery-${project.id}-${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`${project.name} — screenshot ${i + 1}`}
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Next project */}
      {next && (
        <section className="border-t border-[#1A1A1A]/10 py-16 md:py-24 bg-[#1A1A1A] text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <Link
              to={`/project/${next.id}`}
              data-testid="project-next-link"
              className="block group"
            >
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/50">
                Next project
              </div>
              <div className="mt-4 flex items-center justify-between gap-6 flex-wrap">
                <h3 className="font-display font-black tracking-tighter text-4xl sm:text-5xl md:text-7xl lg:text-[92px] leading-[0.95]">
                  {next.name}
                </h3>
                <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#FFE600] text-[#1A1A1A] flex items-center justify-center group-hover:rotate-45 transition-transform duration-500">
                  <ArrowUpRight className="w-6 h-6 md:w-8 md:h-8" strokeWidth={2.5} />
                </span>
              </div>
              <p className="mt-3 text-white/60">{next.tagline}</p>
            </Link>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}
